import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProfile, updateProfile } from "../services/api";
import { optimizeImage } from "../utils/optimizeImage";
import countriesData from "../data/countries";
import { loadEnfermedades } from "../data/enfermedades";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

function ProfileEdit() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language.split("-")[0] || "en";

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [enfermedadesTraducidas, setEnfermedadesTraducidas] = useState([]);

  const [showSuggestions, setShowSuggestions] = useState(false);

  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    country: "",
    ciudad: "",
    comunidad: "",
    date_of_birth: "",
    photo: null,
    bio: "",
    enfermedad: "",
    orphacode: null,
    habla: "",
    escribe: "",
    movilidad: "",
    motricidad: "",
    ver: "",
    audition: "",
    memoire: "",
    orientation: "",
    concentration: "",
  });

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [tipoRegistro, setTipoRegistro] = useState(null);

  useEffect(() => {
    loadEnfermedades(currentLang).then(setEnfermedadesTraducidas);
  }, [currentLang]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        setForm((prev) => ({ ...prev, ...response.data }));
        if (response.data.photo) setPreviewUrl(response.data.photo);
        setTipoRegistro(response.data?.user?.tipo_registro || null);
      } catch (error) {
        if (error.response?.status === 401) {
          alert(t("auth.session_expired"));
          navigate("/login");
        } else {
          alert(t("profile_edit.load_error"));
        }
      } finally {
        setLoading(false);
      }
    };

    const firstVisit = localStorage.getItem("profile_edit_first_visit");
    if (!firstVisit) {
      setShowWelcomeModal(true);
      localStorage.setItem("profile_edit_first_visit", "1");
    }

    setCountries(
      countriesData.map((c) => ({
        code: c.countryCode,
        label: c.countryLabel,
        cities: c.cityCodes,
      }))
    );

    fetchProfile();
  }, [navigate, t]);

  useEffect(() => {
    const selected = countries.find((c) => c.code === form.country);
    setCities(selected ? selected.cities : []);
  }, [form.country, countries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const update = { [name]: value };

    if (name === "enfermedad") {
      const enfermedad = enfermedadesTraducidas.find(
        (enf) => enf.name.toLowerCase().trim() === value.toLowerCase().trim()
      );
      update.orphacode = enfermedad ? enfermedad.code : null;
    }

    setForm((prev) => ({
      ...prev,
      ...update,
      ...(name === "country" ? { ciudad: "" } : {}),
    }));
  };

  // HAPPYCAP_PROFILE_CAMERA_SAVE_FIX_V1
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      if (!file.type.startsWith("image/")) {
        throw new Error("El archivo seleccionado no es una imagen.");
      }

      // Normalizar fotografías de cámara y galería a JPEG.
      const optimized = await optimizeImage(file);

      if (optimized.size > 8 * 1024 * 1024) {
        throw new Error("La fotografía es demasiado grande.");
      }

      setForm((prev) => ({ ...prev, photo: optimized }));
      setPreviewUrl((previous) => {
        if (previous?.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }
        return URL.createObjectURL(optimized);
      });

      console.info(
        "Foto de perfil preparada:",
        optimized.type,
        optimized.size
      );
    } catch (error) {
      console.error("Error procesando foto de perfil:", error);
      toast.error(
        "No se pudo preparar la fotografía. Prueba con otra imagen."
      );
    }
  };

    const isFormValid = () => {
      const { nombre, country, date_of_birth } = form;

      if (!nombre.trim() || !country || !date_of_birth) return false;

      const birthDate = new Date(date_of_birth);
      const today = new Date();

      // Calcular edad
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--; // Ajuste si aún no cumplió años este año
      }

      const isDateInFuture = birthDate > today;
      const hasValidAge = age >= 1 && age <= 100;

      return !isDateInFuture && hasValidAge;
    };

  const handleSave = async () => {
  setMsg("");

  if (!isFormValid()) {
    toast.error(t("profile_edit.tooltip_missing"));
    return;
  }

  // 🔍 Validación de enfermedad
  if (form.enfermedad.trim()) {
    const enfermedad = enfermedadesTraducidas.find(
      (enf) =>
        enf.name.toLowerCase().trim() === form.enfermedad.toLowerCase().trim()
    );

    if (!enfermedad) {
      toast.error(
        t("profile_edit.errors.invalid_disease", "Vous devez sélectionner une maladie valide dans la liste")
      );
      return; // ⛔ detenemos el guardado
    }

    // Aseguramos que nombre y código queden sincronizados
    form.enfermedad = enfermedad.name;
    form.orphacode = enfermedad.code;
  } else {
    // Si está vacío → se guarda "Sans diagnostic"
    form.enfermedad = "Sans diagnostic";
    form.orphacode = null;
  }

  setSaving(true);

    const formData = new FormData();

    for (const key in form) {
      const value = form[key];

      if (key === "photo") {
        if (value instanceof File) {
          formData.append("photo", value);
        }
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    try {
      await updateProfile(formData);
      setMsg(t("profile_edit.save_success"));
      setTimeout(() => navigate("/profile"), 1500);
    } catch (error) {
      if (error.response?.status === 401) {
        alert(t("auth.session_expired"));
        navigate("/login");
      } else {
        console.error("Error guardando perfil:", {
          status: error.response?.status,
          details: error.response?.data,
          message: error.message,
        });

        toast.error(t("profile_edit.save_error"));
      }
    } finally {
      setSaving(false);
    }
  };

const getDateInputProps = () => {
  const today = new Date();

  const subtractDays = (date: Date, days: number) => {
    const copy = new Date(date);
    copy.setDate(copy.getDate() - days);
    return copy;
  };

  const addDays = (date: Date, days: number) => {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
  };

  if (tipoRegistro === "menor") {
    const max = subtractDays(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()), -1); // tiene 1 año menos 1 día
    const min = addDays(new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()), 2); // tiene 17 años y 364 días

    return {
      min: min.toISOString().split("T")[0],
      max: max.toISOString().split("T")[0],
    };
  }

  if (tipoRegistro === "adulto") {
    const max = subtractDays(new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()), -1); // tiene 1 año menos 1 día
    const min = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate()); // hasta 100 años

    return {
      min: min.toISOString().split("T")[0],
      max: max.toISOString().split("T")[0],
    };
  }

  return {};
};




  if (loading)
    return <p className="text-center">{t("profile_edit.loading")}</p>;

return (
  <main className="min-h-screen bg-[#DCECF9] px-3 py-6 sm:px-6 sm:py-10">
    <div className="mx-auto max-w-4xl overflow-hidden rounded-3xl bg-white shadow-xl">
      <div className="h-24 bg-gradient-to-br from-[#F97316] via-[#FDBA74] to-[#FFEDD5] sm:h-32" />
      <div className="px-4 pb-8 sm:px-10">
    {showWelcomeModal && (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-start justify-center pt-24 px-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-lg text-gray-800">
          <h2 className="text-lg font-semibold mb-4">{t("activate_modal.title")}</h2>
          <p className="text-sm mb-4">{t("activate_modal.body1")}</p>
          <p className="text-sm mb-4">{t("activate_modal.body2")}</p>
          <p className="text-sm mb-6">{t("activate_modal.body3")}</p>
          <div className="text-right">
            <button
              onClick={() => setShowWelcomeModal(false)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
            >
              {t("activate_modal.ok")}
            </button>
          </div>
        </div>
      </div>
    )}


<div
  className="relative -mt-10 mb-7 flex cursor-pointer items-center gap-4 rounded-2xl border border-[#E2EEF6] bg-[#F8FBFE] p-4 shadow-sm transition hover:border-[#247BA8] focus-within:ring-2 focus-within:ring-[#247BA8]"
  onClick={() => document.getElementById("photo-upload").click()}
>
  <div>
    {previewUrl ? (
      <img
        src={previewUrl}
        alt="preview"
        className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-md"
      />
    ) : (
      <div className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-[#E4F2FA] text-[#247BA8] shadow-md">
        📷
      </div>
    )}
  </div>
  <div className="flex flex-col">
    <span className="text-sm text-gray-500">{t("profile_edit.photo_label")}</span>
    <span className="cursor-pointer text-base font-semibold text-[#247BA8] hover:underline">
      {t("common.choose_file")}
    </span>
    <input
      id="photo-upload"
      type="file"
      accept="image/*"
      onChange={handleFileChange}
      className="hidden"
    />
  </div>
</div>


    {/* HAPPYCAP_PROFILE_EDIT_HARMONY_V2 */}
    <style>{`
      .hc-modern-field {
        min-width: 0;
      }

      .hc-modern-field > div {
        height: 100%;
        min-width: 0;
        padding: 18px 20px !important;
        border: 1px solid #E3EDF4 !important;
        border-radius: 20px !important;
        background: #F7FAFD !important;
        transition:
          border-color 180ms ease,
          background-color 180ms ease,
          box-shadow 180ms ease !important;
      }

      .hc-modern-field > div:hover {
        border-color: #C9DFED !important;
        background: #F1F8FC !important;
      }

      .hc-modern-field > div:focus-within {
        border-color: #247BA8 !important;
        background: #FFFFFF !important;
        box-shadow: 0 0 0 3px rgba(36,123,168,.12) !important;
      }

      /* HAPPYCAP_PROFILE_EDIT_LABELS_UPPERCASE_V1 */
      .hc-modern-field label {
        display: block !important;
        margin-bottom: 10px !important;
        color: #648098 !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        letter-spacing: 0.025em !important;
        line-height: 1.5 !important;
        text-transform: uppercase !important;
        font-family: inherit !important;
      }

      .hc-modern-field input,
      .hc-modern-field select {
        width: 100% !important;
        min-height: 44px !important;
        padding: 8px 2px !important;
        color: #245580 !important;
        font-size: 15px !important;
        font-weight: 600 !important;
        line-height: 1.5 !important;
        background: transparent !important;
        border: 0 !important;
        outline: 0 !important;
        box-shadow: none !important;
      }

      .hc-modern-field input::placeholder {
        color: #94A3B8 !important;
        font-weight: 400 !important;
      }

      /* HAPPYCAP_PROFILE_EDIT_SELECT_CARDS_V3 */
      .hc-modern-field:has(select) {
        position: relative;
      }

      .hc-modern-field:has(select) > div {
        cursor: pointer;
      }

      .hc-modern-field select {
        cursor: pointer;
        appearance: none !important;
        -webkit-appearance: none !important;
        padding-right: 28px !important;
        position: relative;
        z-index: 1;
      }

      .hc-modern-field:has(select)::after {
        content: "⌄";
        position: absolute;
        right: 20px;
        bottom: 22px;
        color: #247BA8;
        font-size: 24px;
        line-height: 1;
        pointer-events: none;
      }

      .hc-modern-field input:focus,
      .hc-modern-field select:focus {
        outline: 0 !important;
        box-shadow: none !important;
      }

      @media (prefers-reduced-motion: reduce) {
        .hc-modern-field > div {
          transition: none !important;
        }
      }
    `}</style>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="hc-modern-field">
        {renderCardInput("nombre", t("common.first_name"))}
      </div>
      <div className="hc-modern-field">
        {renderCardInput("apellidos", t("common.last_name"))}
      </div>
      <div className="hc-modern-field">
        {renderCardSelect("country", t("search_profile.country"), countries, "label", "code")}
      </div>
      <div className="hc-modern-field">
        {renderCardSelect("ciudad", t("search_profile.city"), cities)}
      </div>
      <div className="hc-modern-field">
        {renderCardInput("comunidad", t("search_profile.community"))}
      </div>
      <div className="hc-modern-field">
        {renderCardInput("date_of_birth", t("common.birth_date"), "date")}
      </div>
      {renderCardTextarea("bio", t("profile.bio"))}

        <div className="relative col-span-full rounded-2xl border border-[#E2EEF6] bg-[#F8FBFE] p-4 transition focus-within:border-[#247BA8] focus-within:ring-2 focus-within:ring-[#247BA8]/20">
	  <label htmlFor="enfermedad" className="mb-2 block text-sm font-medium text-[#245580]">
	    🩺 {t("search_disease.placeholder")}
	  </label>


	<input
	  name="enfermedad"
	  id="enfermedad"
	  value={form.enfermedad}
	  onChange={(e) => {
	    handleChange(e);
	    setShowSuggestions(true); // mostrar solo mientras se escribe
	  }}
	  onFocus={() => setShowSuggestions(true)} // mostrar si enfoco y hay coincidencias
	  className="w-full rounded-lg border-0 bg-transparent text-base text-slate-800 outline-none focus:ring-0"
	  autoComplete="off"
	/>

	{showSuggestions &&
	  form.enfermedad &&
	  (enfermedadesTraducidas || []).filter(
	    (e) =>
	      e.name.toLowerCase().includes(form.enfermedad.toLowerCase()) &&
	      e.name.toLowerCase() !== form.enfermedad.toLowerCase()
	  ).length > 0 && (
	    <ul className="absolute left-0 right-0 z-50 mt-2 max-h-48 overflow-y-auto rounded-xl border border-[#C7DDEB] bg-white shadow-xl">
	      {(enfermedadesTraducidas || [])
		.filter(
		  (e) =>
		    e.name.toLowerCase().includes(form.enfermedad.toLowerCase()) &&
		    e.name.toLowerCase() !== form.enfermedad.toLowerCase()
		)
		.map((e) => (
		  <li
		    key={e.code}
		    onClick={() => {
		      setForm((prev) => ({ ...prev, enfermedad: e.name, orphacode: e.code, }));
		      setShowSuggestions(false); // 👈 cerrar lista al seleccionar
		    }}
		    className="cursor-pointer px-4 py-3 text-sm hover:bg-[#E6F3FC]"
		  >
		    {e.name}
		  </li>
		))}
	    </ul>
	  )}
	</div>


      {renderFeatureChoices("habla", "🗣️", t("profile.habla"), ["non verbal", "verbal", "caa"])}
      {renderFeatureChoices("escribe", "✍️", t("profile.escribe"), ["non", "oui", "caa"])}
      {renderFeatureChoices("movilidad", "🚶", t("profile.movilidad"), ["lit", "fauteuil roulant", "béquilles", "sans support"])}
      {renderFeatureChoices("motricidad", "🤲", t("profile.motricidad"), ["non", "spasticité", "imprécise", "ordinaire", "mouv.anormaux"])}
      {renderFeatureChoices("ver", "👁️", t("profile.ver"), ["voyante", "aveugle"])}
      {renderFeatureChoices("audition", "👂", t("profile.audition"), ["sans", "faible", "ordinaire", "hypersensible"])}
      {renderFeatureChoices("memoire", "🧠", t("profile.memoire"), ["sans", "faible", "ordinaire"])}
      {renderFeatureChoices("orientation", "🧭", t("profile.orientation"), ["sans", "faible", "ordinaire"])}
      {renderFeatureChoices("concentration", "🧩", t("profile.concentration"), ["sans", "ephémere", "partial", "durable"])}

    <div className="col-span-full mt-7 border-t border-slate-100 pt-7 text-center">
      <button
        id="save-profile-button"
        onClick={handleSave}
        disabled={saving}
        className={`bg-[#F97316] hover:bg-[#EA580C] text-white py-3 px-8 rounded-2xl font-semibold shadow-sm transition ${
          !isFormValid() ? "cursor-not-allowed opacity-60" : ""
        }`}
        data-tooltip-id="save-tooltip"
        data-tooltip-content={
          !isFormValid() ? t("profile_edit.tooltip_missing") : ""
        }
      >
        {saving ? t("common.saving") : t("common.save")}
      </button>
        {msg && (
  <p
    className={`mt-3 text-sm font-medium ${
      msg.toLowerCase().includes("success") || msg.toLowerCase().includes("succès")
        ? "text-green-600"
        : "text-red-600"
    }`}
  >
    {msg}
  </p>
)}

  <Tooltip
    id="save-tooltip"
    place="top"
    className="!bg-yellow-100 !text-yellow-800 !text-sm !rounded-md !px-3 !py-1 shadow-md z-50"
  />
    </div>

    </div>
      </div>
    </div>
  </main>
);

function renderCardInput(name, label, type = "text") {
  return (
    <div className="rounded-2xl border border-[#E2EEF6] bg-[#F8FBFE] p-4 transition focus-within:border-[#247BA8] focus-within:ring-2 focus-within:ring-[#247BA8]/20">
      <label htmlFor={name} className="mb-2 block text-sm font-medium text-[#245580]">{label}</label>
      <input
        key={tipoRegistro || "unknown"}
        id={name}
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        placeholder={label}
        className="w-full rounded-lg border-0 bg-transparent text-base text-slate-800 outline-none focus:ring-0"
        {...(name === "date_of_birth" ? getDateInputProps() : {})}
      />
    </div>
  );
}


function renderCardTextarea(name, label) {
  return (
    <div className="col-span-full rounded-2xl border border-[#E2EEF6] bg-[#F8FBFE] p-4 transition focus-within:border-[#247BA8] focus-within:ring-2 focus-within:ring-[#247BA8]/20">
      <label htmlFor={name} className="mb-2 block text-sm font-medium text-[#245580]">{label}</label>
      <textarea
        id={name}
        name={name}
        value={form[name]}
        onChange={handleChange}
        placeholder={label}
        className="w-full resize-y rounded-lg border-0 bg-transparent text-base leading-relaxed text-slate-800 outline-none focus:ring-0"
        rows={3}
      />
    </div>
  );
}

// HAPPYCAP_PROFILE_EDIT_CHOICES_V1
function renderFeatureChoices(name, emoji, label, options) {
  return (
    <fieldset className="min-w-0 rounded-[22px] border border-[#E3EDF4] bg-[#F7FAFD] p-4 sm:p-5">
      <legend className="sr-only">{label}</legend>

      <div className="mb-4 flex items-center gap-3">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E1EDF5] bg-white text-xl shadow-sm"
          aria-hidden="true"
        >
          {emoji}
        </span>

        <span className="text-xs font-semibold uppercase tracking-wide text-[#648098]">
          {label.replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, "").trim()}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = form[name] === option;

          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              onClick={() =>
                handleChange({
                  target: { name, value: option }
                })
              }
              className={`min-h-11 max-w-full break-words rounded-xl border px-3.5 py-2.5 text-left text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8] focus-visible:ring-offset-2 ${
                selected
                  ? "border-[#247BA8] bg-[#247BA8] text-white shadow-sm"
                  : "border-[#D8E7F0] bg-white text-[#245580] hover:border-[#247BA8] hover:bg-[#EDF7FD]"
              }`}
            >
              {selected && (
                <span className="mr-1.5" aria-hidden="true">
                  ✓
                </span>
              )}
              {option}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function renderCardSelect(name, label, options, labelKey = null, valueKey = null) {
  return (
    <div
      className="rounded-2xl border border-[#E2EEF6] bg-[#F8FBFE] p-4 transition focus-within:border-[#247BA8] focus-within:ring-2 focus-within:ring-[#247BA8]/20"
      onClick={(event) => {
        if (name !== "country" && name !== "ciudad") return;
        if (event.target.closest("select")) return;

        const select = event.currentTarget.querySelector("select");
        if (!select) return;

        select.focus();

        try {
          if (typeof select.showPicker === "function") {
            select.showPicker();
          } else {
            select.click();
          }
        } catch {
          // Algunos navegadores no permiten abrirlo mediante JS.
          // El selector sigue funcionando al pulsarlo directamente.
        }
      }}
    >
      <label htmlFor={name} className="mb-2 block text-sm font-medium text-[#245580]">{label}</label>
      <select
        id={name}
        name={name}
        value={form[name]}
        onChange={handleChange}
        className="w-full rounded-lg border-0 bg-transparent text-base text-slate-800 outline-none focus:ring-0"
      >
        <option value="">{label}</option>
        {options.map((opt) => (
          <option key={labelKey ? opt[valueKey] : opt} value={valueKey ? opt[valueKey] : opt}>
            {labelKey ? opt[labelKey] : opt}
          </option>
        ))}
      </select>
    </div>
  );
}

}
export default ProfileEdit;
